<template>
  <SettingEditLayout
    title="勤怠計算・打刻"
    summary="勤務表と打刻の、会社共通の計算ルールです。"
    :fields="fields"
    :validate="validate"
  >
    <template #default="{ form }">
      <CAlert color="info">
        決めていないときは、打刻猶予なし・時間外1分単位・規定勤務8時間・
        規定休憩1時間で計算します。<br/>
        <b>切り上げ単位は、時間外を働いた人に有利な側へ丸めるためだけに使います。</b>
        実際に働いた時間を切り捨てることはありません。
      </CAlert>

      <div class="time-grid">
        <div v-for="field in timeFields" :key="field.key" class="time-grid__item">
          <label :for="'attendance-' + field.key">
            {{ field.label }}<span class="required">必須</span>
          </label>
          <CInput
            :id="'attendance-' + field.key"
            v-model="form[field.key]"
            placeholder="00:00"
          />
          <small>{{ field.description }}</small>
        </div>
      </div>

      <div class="switch-grid">
        <label class="switch-card" for="post-work-break">
          <input id="post-work-break" type="checkbox" v-model="form.post_work_break_flg"/>
          <span>
            <strong>定時後の猶予時間を休憩として扱う</strong>
            <small>定時後打刻猶予時間を、時間外のときの追加休憩として勤務時間から除きます。</small>
          </span>
        </label>
        <label class="switch-card" for="project-break">
          <input id="project-break" type="checkbox" v-model="form.use_project_break_flg"/>
          <span>
            <strong>プロジェクト休憩を自動で反映する</strong>
            <small>プロジェクトに設定した休憩時間を勤務表へ入れます。</small>
          </span>
        </label>
        <label class="switch-card" for="half-day-leave">
          <input id="half-day-leave" type="checkbox" v-model="form.half_day_leave_flg"/>
          <span>
            <strong>半日単位の休暇を使う</strong>
            <small>
              休暇申請で午前・午後を選べるようになり、勤務表の勤怠区分に
              午前有給・午後有給・午前代休・午後代休・午前特休・午後特休・
              午前欠勤・午後欠勤が出ます。
              切ると新しく選べなくなりますが、<b>すでに保存されている半休はそのまま残ります。</b>
              切ったあと午前だけ休んだ日は、実際の出退勤時刻を入れて
              <b>遅刻・早退として時間を控除します。</b>控除額は半日欠勤と同じです。
            </small>
          </span>
        </label>
      </div>

      <div class="expiry-block">
        <label class="switch-card" for="substitute-leave-expiry">
          <input
            id="substitute-leave-expiry"
            type="checkbox"
            v-model="form.substitute_leave_expiry_flg"
          />
          <span>
            <strong>代休に期限を設ける</strong>
            <small>
              休日出勤で発生した代休を、一定の期間で消滅させます。
              <b>期限は発生した月の末日から数えます。</b>
              8月に発生した代休を2か月とした場合、10月31日まで使えます。
              切っているあいだは、代休は消滅しません（これまでどおり）。
            </small>
          </span>
        </label>

        <div v-if="form.substitute_leave_expiry_flg" class="expiry-months">
          <label for="substitute-leave-expiry-months">
            消滅までの月数<span class="required">必須</span>
          </label>
          <CInput
            id="substitute-leave-expiry-months"
            v-model="form.substitute_leave_expiry_months"
            type="number"
            min="1"
            max="60"
          />
          <small>
            1から60まで。発生した月の末日から数えるので、
            <b>決めた月数より短くなることはありません。</b>
            すでに発生している代休の期限は、ここを変えても動きません。
          </small>
        </div>
      </div>
    </template>
  </SettingEditLayout>
</template>

<script>
import SettingEditLayout from '../SettingEditLayout.vue'
import { findSection } from '../settingSections'

const TIME_PATTERN = /^([0-9]|[01][0-9]|2[0-3]):[0-5][0-9]$/

export default {
  name: 'AttendanceRuleEdit',
  components: { SettingEditLayout },

  data () {
    return {
      fields: findSection('attendance').fields,
      timeFields: [
        { key: 'pre_buffer', label: '定時前打刻猶予時間', description: '決めなければ00:00。打刻の猶予として使います。' },
        { key: 'post_buffer', label: '定時後打刻猶予時間', description: '決めなければ00:00。追加休憩を使うときもこの値です。' },
        { key: 'work_time_unit', label: '時間外の切り上げ単位', description: '決めなければ00:01。00:00にはできません。' },
        { key: 'work_basic', label: '規定勤務時間', description: '決めなければ08:00。所定が違う会社は変えてください。' },
        { key: 'break_basic', label: '規定休憩時間', description: '決めなければ01:00。実際に取る休憩に合わせてください。' }
      ]
    }
  },

  methods: {
    validate (form) {
      for (const field of this.timeFields) {
        const value = String(form[field.key] || '').trim()
        if (!value) return field.label + 'を入力してください。'
        if (!TIME_PATTERN.test(value)) {
          return field.label + 'は「時:分」（例 01:30）で入力してください。'
        }
      }
      // **0にはできない。**0にすると、丸める単位が無くなって
      // 時間外が1分も積まれない月ができる。
      if (String(form.work_time_unit).trim() === '00:00') {
        return '時間外の切り上げ単位に00:00は指定できません。'
      }
      // **期限を使うときだけ月数を見る。**使わない設定のときに
      // 空欄で止めると、代休の期限を切りたいだけの人が保存できない。
      if (form.substitute_leave_expiry_flg) {
        const months = Number(String(form.substitute_leave_expiry_months).trim())
        if (!Number.isInteger(months) || months < 1 || months > 60) {
          return '代休が消滅するまでの月数は、1から60までの整数で入力してください。'
        }
      }
      return ''
    }
  }
}
</script>

<style scoped>
.time-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.time-grid__item label {
  display: block;
  font-weight: 600;
}

.time-grid__item small {
  color: #5c6873;
}

.required {
  margin-left: 0.4rem;
  padding: 0.1rem 0.4rem;
  border-radius: 0.2rem;
  background: #e55353;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 600;
}

.switch-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(18rem, 1fr));
  gap: 0.75rem;
  margin-top: 1.5rem;
}

.switch-card {
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  padding: 0.9rem 1rem;
  border: 1px solid #d8dbe0;
  border-radius: 0.35rem;
  cursor: pointer;
}

.switch-card strong { display: block; }
.switch-card small { color: #5c6873; }

.expiry-block {
  margin-top: 0.75rem;
}

/* 月数はスイッチにぶら下がる入力なので、字下げして関係を見せる。 */
.expiry-months {
  margin: 0.75rem 0 0 1.5rem;
  max-width: 15rem;
}

.expiry-months label {
  display: block;
  font-weight: 600;
}

.expiry-months small {
  color: #5c6873;
}
</style>
